import axios from 'axios';
import { inUscita } from '../utils/uscitaSicura';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: { 'Content-Type': 'application/json' },
    timeout: 30000,
});

/* LE RICHIESTE IN VOLO (2/10/2026), per la ricarica automatica
   (utils/versioneInLinea.js): con un salvataggio a meta' o un file che sta
   salendo la pagina non si ricarica. Si contano a parte le SCRITTURE: al
   cambio di pagina le letture della pagina nuova la ricarica le rifa'.

   L'ORDINE CONTA, ed e' il motivo per cui questi due intercettori stanno
   PRIMA degli altri: le richieste li eseguono in ordine inverso, quindi
   questo gira per ultimo, subito prima dell'invio (niente puo' fallire fra
   il +1 e la partenza); le risposte nell'ordine scritto, quindi il -1 lo
   fa questo, prima che l'intercettore qui sotto sostituisca l'errore col
   corpo della risposta e la configurazione vada persa.

   IL SEGNO STA IN UN SIMBOLO SULLA CONFIGURAZIONE, non in una mappa che la
   usa come chiave: axios (dispatchRequest) la ricopia in un oggetto nuovo
   prima di spedirla, e la risposta porta la copia. Le proprieta' e i
   simboli passano nella copia, l'identita' no. E un segno piu' vecchio di
   due minuti non conta piu': ogni richiesta ha 30 secondi (timeout qui
   sopra), quindi e' una richiesta di cui si e' perso il ritorno, e da sola
   fermerebbe la ricarica per sempre. */
const IN_VOLO = Symbol('rimedichub.inVolo');
const inVolo = new Set();
const PERSA_DOPO_MS = 2 * 60 * 1000;
const vive = () => {
    const limite = Date.now() - PERSA_DOPO_MS;
    let perse = 0;
    inVolo.forEach((t) => { if (t.partita < limite) { inVolo.delete(t); perse += 1; } });
    if (perse && inVolo.size === 0) annunciaQuiete();
    return [...inVolo];
};
const finita = (config) => {
    if (config && config[IN_VOLO]) inVolo.delete(config[IN_VOLO]);
    if (vive().length === 0) annunciaQuiete();
};
export const richiesteInVolo = () => vive().length;
export const scrittureInVolo = () => vive().filter((t) => t.scrittura).length;

/* ALLA QUIETE: chi aspetta che le richieste in volo tornino a zero viene
   chiamato appena succede, invece di aspettare il suo prossimo timer
   (revisione del 2/10, per utils/versioneInLinea.js). MISURATO, ed e' il
   motivo: in una scheda nascosta da qualche minuto Chromium sveglia i timer
   una volta al minuto TUTTI INSIEME, e le letture periodiche degli avvisi del
   Layout (AlertNotifier, RitardiNotifier, AttesaNotifier: 60 s) partono
   nello stesso istante del controllo della versione; a pagina ferma i due
   setInterval da 60 s nascono nello stesso giro di React. La HEAD sui file
   nuovi finiva prima delle letture, la ricarica si fermava su «richiesta» a
   ogni giro, e al giro dopo tutto si ripeteva: una scheda nascosta da tempo
   non si aggiornava mai. Le risposte non sono rallentate come i timer.

   A COLPO SINGOLO: chi viene chiamato ricontrolla tutto da capo, e se deve
   aspettare ancora si rimette in fila. Lo stesso richiamo registrato due
   volte vale una (un Set).

   IN UN COMPITO A SE', DOPO la risposta: il messaggio parte da qui, che e'
   il primo intercettore delle risposte, quindi arriva quando chi aveva
   chiesto ha gia' avuto la sua risposta in mano - chi scarica un file ha
   gia' fatto partire lo scaricamento, chi fa partire un'altra richiesta l'ha
   gia' fatta contare (e allora chi aspetta resta in fila). MessageChannel
   e non setTimeout: un timer, in una scheda nascosta, aspetterebbe il
   risveglio del minuto dopo.
   Il canale nasce solo quando serve: in Node (rotte:runtime) una porta con
   un ascoltatore terrebbe acceso il processo, quindi si sgancia (unref). */
const allaQuieteInFila = new Set();
let quieteAnnunciata = false;
let canaleQuiete = null;
function suonaQuiete() {
    quieteAnnunciata = false;
    if (vive().length > 0) return;          // ne e' partita un'altra: si aspetta la sua fine
    const chi = [...allaQuieteInFila];
    allaQuieteInFila.clear();
    chi.forEach((fn) => {
        try { fn(); } catch (e) { /* chi aspetta non deve far cadere gli altri */ }
    });
}
function annunciaQuiete() {
    if (quieteAnnunciata || allaQuieteInFila.size === 0) return;
    quieteAnnunciata = true;
    if (!canaleQuiete && typeof window !== 'undefined' && typeof window.MessageChannel === 'function') {
        canaleQuiete = new window.MessageChannel();
        canaleQuiete.port1.onmessage = suonaQuiete;
        if (typeof canaleQuiete.port1.unref === 'function') canaleQuiete.port1.unref();
    }
    if (canaleQuiete) canaleQuiete.port2.postMessage(0);
    else setTimeout(suonaQuiete, 0);
}
export function allaQuiete(fn) {
    allaQuieteInFila.add(fn);
    if (vive().length === 0) annunciaQuiete();
}

api.interceptors.request.use((config) => {
    /* Sempre un segno nuovo: una configurazione rimandata (api(e.config))
       porta ancora quello della volta prima, ormai tolto. */
    const t = { partita: Date.now(), scrittura: !/^(get|head|options)$/i.test(config.method || 'get') };
    config[IN_VOLO] = t;
    inVolo.add(t);
    return config;
});
api.interceptors.response.use(
    (response) => { finita(response && response.config); return response; },
    (error) => { finita(error && error.config); return Promise.reject(error); },
);

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    /* La pagina se ne sta andando (utils/uscitaSicura.js): il salvataggio
       del campo che si stava scrivendo deve arrivare anche a pagina
       chiusa. `keepalive` lo chiede al browser, e lo sa fare solo fetch. */
    if (inUscita()) {
        config.adapter = 'fetch';
        config.fetchOptions = { ...(config.fetchOptions || {}), keepalive: true };
    }
    return config;
}, (error) => Promise.reject(error));

/* 401 VUOL DIRE UNA COSA SOLA: la sessione non vale piu'. Si chiude e
   si torna all'accesso, senza eccezioni.
   Fino al 21 settembre 2026 qui c'erano due liste di rotte «dove il 401
   non e' una sessione» - login, recupero password, verifica del secondo
   fattore - perche' il server rispondeva 401 anche a una password o a
   un codice sbagliati. Le liste di eccezioni crescono, e la rotta che
   nessuno aggiunge sloga l'utente a caso. Adesso e' il server a non
   rispondere 401 per quei motivi: risponde 422 con un `code`. Se un
   giorno una rotta nuova torna a farlo, il sintomo e' un logout
   inspiegabile, e la correzione sta nel server, non in una lista qui. */
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        // Il backend blocca tutte le route finche' la password provvisoria
        // non viene sostituita: qui riportiamo l'utente sulla schermata giusta.
        if (error.response?.status === 403 && error.response?.data?.code === 'PASSWORD_DA_CAMBIARE') {
            if (window.location.pathname !== '/cambia-password') {
                window.location.href = '/cambia-password';
            }
        }
        // Stesso schema per l'obbligo del secondo fattore a periodo di
        // grazia finito: il server lascia aperte solo le rotte
        // dell'attivazione, e qui si porta l'utente dove puo' farla.
        if (error.response?.status === 403 && error.response?.data?.code === 'SERVE_ATTIVARE_2FA') {
            if (window.location.pathname !== '/profilo') {
                window.location.href = '/profilo';
            }
        }

        // Questo interceptor rigetta il BODY della risposta, non l'oggetto
        // errore di axios. Alcuni componenti pero' leggono il messaggio come
        // e.response.data.message (la forma di axios): senza questo innesto
        // troverebbero undefined e mostrerebbero un messaggio generico al
        // posto di quello del backend.
        //
        // La proprieta' e' non enumerabile: non compare negli spread ne'
        // in JSON.stringify, e la copia evita riferimenti circolari.
        const corpo = error.response?.data || { success: false, message: 'Errore di connessione' };
        if (corpo && typeof corpo === 'object' && !corpo.response) {
            Object.defineProperty(corpo, 'response', {
                value: { status: error.response?.status, data: { ...corpo } },
                enumerable: false,
                configurable: true,
            });
        }
        return Promise.reject(corpo);
    }
);

export const authAPI = {
    login: (credentials) => api.post('/auth/login', credentials),
    getProfile: () => api.get('/auth/me'),
    cambiaPassword: (data) => api.post('/auth/cambia-password', data),
    passwordDimenticata: (data) => api.post('/auth/password-dimenticata', data),
    reimpostaPassword: (data) => api.post('/auth/reimposta-password', data),
};

/* La verifica in due passaggi.
   `verifica` e' l'unica che si chiama senza sessione: porta il gettone
   provvisorio nel corpo, non nell'intestazione, perche' una sessione
   ancora non c'e'. */
export const secondoFattoreAPI = {
    stato: () => api.get('/auth/2fa/stato'),
    avvia: () => api.post('/auth/2fa/avvia'),
    conferma: (codice) => api.post('/auth/2fa/conferma', { codice }),
    verifica: (dati) => api.post('/auth/2fa/verifica', dati),
    rigeneraCodici: (password, codice) => api.post('/auth/2fa/codici', { password, codice }),
    disattiva: (password, codice) => api.post('/auth/2fa/disattiva', { password, codice }),
};

export const pazientiAPI = {
    getAll: (params) => api.get('/pazienti', { params }),
    getById: (id) => api.get(`/pazienti/${id}`),
    create: (data) => api.post('/pazienti', data),
    update: (id, data) => api.put(`/pazienti/${id}`, data),
    updateNote: (id, note) => api.patch(`/pazienti/${id}/note`, { note }),
    /* Due campi distinti, e non e' un dettaglio: il medico curante e'
       UNO e lo dichiara il paziente in accettazione; gli altri
       professionisti sono un testo libero. Nessuno dei due e' la tabella
       `medici`, che e' l'elenco dei collaboratori della clinica. */
    updateAltriProfessionisti: (id, testo) =>
        api.patch(`/pazienti/${id}/altri-professionisti`, { altri_professionisti: testo }),
    updateMedicoCurante: (id, dati) =>
        api.patch(`/pazienti/${id}/medico-curante`, dati),
    delete: (id) => api.delete(`/pazienti/${id}`),
    exportCSV: () => api.get('/pazienti/export/csv', { responseType: 'blob' }),
    exportVCard: () => api.get('/pazienti/export/vcard', { responseType: 'blob' }),
};

export default api;

export const anamnesiAPI = {
    getByPazienteId: (pazienteId) => api.get(`/anamnesi/${pazienteId}`),
    createOrUpdate: (pazienteId, data) => api.post(`/anamnesi/${pazienteId}`, data),
    getAnnotazioni: (pazienteId, tipo) => api.get(`/anamnesi/${pazienteId}/annotazioni`, { params: { tipo } }),
    createAnnotazione: (pazienteId, data) => api.post(`/anamnesi/${pazienteId}/annotazioni`, data),
    deleteAnnotazione: (id) => api.delete(`/anamnesi/annotazioni/${id}`),
};

export const visiteAPI = {
    getByPazienteId: (pazienteId) => api.get(`/visite/paziente/${pazienteId}`),
    getById: (id) => api.get(`/visite/${id}`),
    create: (pazienteId, data) => api.post(`/visite/paziente/${pazienteId}`, data),
    /* Fase 2 del riordino: una procedura in piu' nella visita di oggi, e la
       spunta «Includi nel referto» del suo riquadro. */
    aggiungiPrestazione: (visitaId, data) => api.post(`/visite/${visitaId}/prestazioni`, data),
    includiNelReferto: (visitaId, rigaId, includi) =>
        api.patch(`/visite/${visitaId}/prestazioni/${rigaId}/referto`, { includi }),
    update: (id, data) => api.put(`/visite/${id}`, data),
    /* «Togli» una prestazione dalla visita (migration 298): il motivo serve
       all'amministrazione; chi puo' e quando lo decide il server. */
    togliPrestazione: (visitaId, rigaId, motivo) =>
        api.delete(`/visite/${visitaId}/prestazioni/${rigaId}`, { data: motivo ? { motivo } : {} }),
    /* Il cestino delle visite (migration 291): `prodotti_usati` e' la
       risposta alla domanda che il server fa (409, domanda 'prodotti_usati')
       quando dalla visita sono usciti prodotti dal magazzino. */
    delete: (id, prodottiUsati) => api.delete(`/visite/${id}`,
        typeof prodottiUsati === 'boolean' ? { data: { prodotti_usati: prodottiUsati } } : undefined),
    /* `dati` porta { urgenza, motivo } quando si apre senza
       autorizzazione dichiarando un'urgenza clinica. Senza, apre solo
       chi e' autorizzato e la risposta e' 403 per gli altri. */
    apriRiservata: (id, dati) => api.post(`/visite/${id}/apri-riservata`, dati || {}),
    aperture: () => api.get('/visite/aperture'),
    /* «Le ho viste»: scrive `notificato_il`, la colonna che dal 2026
       dichiarava che il medico esecutore era stato avvisato e che
       nessuno scriveva. */
    apertureViste: () => api.post('/visite/aperture/viste'),

    // Autorizzazioni alle procedure riservate
    richiediAccesso: (id, motivo) =>
        api.post(`/visite/${id}/richiedi-accesso`, { motivo }),
    richiesteAccesso: () => api.get('/visite/richieste-accesso'),
    decidiRichiesta: (id, concessa, note) =>
        api.post(`/visite/richieste-accesso/${id}/decidi`, { concessa, note }),

    // Moduli
    getModuli: (visitaId) => api.get(`/visite/${visitaId}/moduli`),
    createModulo: (visitaId, data) => api.post(`/visite/${visitaId}/moduli`, data),
    updateModulo: (id, data) => api.put(`/visite/moduli/${id}`, data),
    deleteModulo: (id) => api.delete(`/visite/moduli/${id}`),
};

export const mediciAPI = {
    getAll: (soloAttivi = false) => api.get(`/medici${soloAttivi ? '?soloAttivi=true' : ''}`),
    getById: (id) => api.get(`/medici/${id}`),
    create: (data) => api.post('/medici', data),
    update: (id, data) => api.put(`/medici/${id}`, data),
    delete: (id) => api.delete(`/medici/${id}`),
    getScadenze: () => api.get('/medici/scadenze-assicurazioni'),
    riferimenti: (id) => api.get(`/medici/${id}/riferimenti`),
};

export const prestazioniAPI = {
    getAll: (soloAttive = false) => api.get(`/prestazioni${soloAttive ? '?soloAttive=true' : ''}`),
    getById: (id) => api.get(`/prestazioni/${id}`),
    getAliquote: () => api.get('/prestazioni/aliquote'),
    getTipiSpesa: () => api.get('/prestazioni/tipi-spesa'),
    getMacroAree: () => api.get('/prestazioni/macro-aree'),
    create: (data) => api.post('/prestazioni', data),
    update: (id, data) => api.put(`/prestazioni/${id}`, data),
    delete: (id) => api.delete(`/prestazioni/${id}`),
    /* D12: i pacchetti della voce le cui prossime rate uscirebbero con
       un'aliquota diversa dalle rate gia' in fattura, con questa aliquota. */
    ratePacchetti: (id, aliquotaId) =>
        api.get(`/prestazioni/${id}/rate-pacchetti`, { params: { aliquota_id: aliquotaId } }),
    /* Quanti pacchetti sono gia' venduti con la voce, e la frase che lo
       dice: la compone il server, il modulo la mostra com'e'. */
    pacchettiVenduti: (id) => api.get(`/prestazioni/${id}/pacchetti-venduti`),
};

export const spettanzeAPI = {
    getRiepilogo: (params = {}) => api.get('/spettanze/riepilogo', { params }),
    getDettaglio: (params = {}) => api.get('/spettanze/dettaglio', { params }),
};

export const anamnesiFisiologicaAPI = {
    getDettaglio: (pazienteId) => api.get(`/anamnesi/${pazienteId}/fisiologica-dettaglio`),
    /* `origine` dice da dove si scrive il riquadro delle gravidanze
       (anamnesi o scheda ginecologica): lo registra il database accanto
       all'autore dell'ultima modifica. */
    saveDettaglio: (pazienteId, data, params) =>
        api.post(`/anamnesi/${pazienteId}/fisiologica-dettaglio`, data, params ? { params } : undefined),
    /* Il riquadro delle gravidanze al giorno di una visita: { compilazione }
       oppure { visita }. Risponde se e' modificabile (oggi) e il valore. */
    getGravidanzeAl: (pazienteId, params) =>
        api.get(`/anamnesi/${pazienteId}/gravidanze-al`, { params }),
};

/* Fumo e alcol (proposta 16): l'ultimo valore coi precedenti, la scrittura
   di UN campo (la versione nuova la compone il server), e il valore al
   giorno di una scheda, { compilazione } oppure { visita }. */
export const abitudiniAPI = {
    leggi: (pazienteId) => api.get(`/anamnesi/${pazienteId}/abitudini`),
    scrivi: (pazienteId, campo, valore) => api.put(`/anamnesi/${pazienteId}/abitudini`, { campo, valore }),
    alGiorno: (pazienteId, params) => api.get(`/anamnesi/${pazienteId}/abitudini-al`, { params }),
};

export const procedureAPI = {
    getRecap: (pazienteId) => api.get(`/procedure/paziente/${pazienteId}`),
    getDettaglio: (pazienteId, nome) => api.get(`/procedure/paziente/${pazienteId}/dettaglio`, { params: { nome } }),
};

export const alertAPI = {
    getByPaziente: (pazienteId) => api.get(`/alert/paziente/${pazienteId}`),
    create: (pazienteId, data) => api.post(`/alert/paziente/${pazienteId}`, data),
    update: (id, data) => api.put(`/alert/${id}`, data),
    remove: (id) => api.delete(`/alert/${id}`),
    getDue: () => api.get('/alert/due'),
    markNotificato: (id) => api.put(`/alert/${id}/notificato`),
};

export const fotoAPI = {
    getByPaziente: (pazienteId) => api.get(`/foto/paziente/${pazienteId}`),
    // Content-Type undefined -> il browser imposta multipart/form-data col boundary corretto
    upload: (pazienteId, formData) => api.post(`/foto/paziente/${pazienteId}`, formData, {
        headers: { 'Content-Type': undefined },
    }),
    remove: (id) => api.delete(`/foto/${id}`),
};

export const rxAPI = {
    // L'abilitazione sta sul medico: la schermata chiede al server se deve
    // mostrare il tasto. Non e' la protezione - quella e' su ogni rotta -
    // e' il non mettere in menu una cosa che poi risponderebbe 403.
    accesso: () => api.get('/rx/accesso'),
    perPaziente: (pazienteId) => api.get(`/rx/paziente/${pazienteId}`),
    crea: (pazienteId, data) => api.post(`/rx/paziente/${pazienteId}`, data),
    modifica: (id, data) => api.put(`/rx/${id}`, data),
    elimina: (id) => api.delete(`/rx/${id}`),
    elenco: (params) => api.get('/rx', { params }),
    mediciAbilitati: () => api.get('/rx/medici'),
    esporta: (params) => api.get('/rx/esporta', { params, responseType: 'blob' }),
};

export const campiAPI = {
    aree: () => api.get('/campi/aree'),
    scale: () => api.get('/campi/scale'),
    elenco: (params) => api.get('/campi', { params }),
    crea: (data) => api.post('/campi', data),
    modifica: (id, data) => api.put(`/campi/${id}`, data),
    ritira: (id) => api.post(`/campi/${id}/ritira`),
    riattiva: (id) => api.post(`/campi/${id}/riattiva`),
    proposte: () => api.get('/campi/proposte'),
    proponi: (data) => api.post('/campi/proposte', data),
    decidi: (id, data) => api.post(`/campi/proposte/${id}/decidi`, data),
};

export const schedeAPI = {
    elenco: () => api.get('/schede'),
    dettaglio: (id) => api.get(`/schede/${id}`),
    crea: (data) => api.post('/schede', data),
    modifica: (id, data) => api.put(`/schede/${id}`, data),
    // Attivare, disattivare, ordinare, assegnare a un'area: e' quello che
    // il server concede anche alla segreteria. Tutto il resto no.
    anagrafica: (id, data) => api.patch(`/schede/${id}/anagrafica`, data),
    creaSezione: (id, data) => api.post(`/schede/${id}/sezioni`, data),
    modificaSezione: (id, data) => api.put(`/schede/sezioni/${id}`, data),
    eliminaSezione: (id) => api.delete(`/schede/sezioni/${id}`),
    aggiungiCampo: (id, data) => api.post(`/schede/${id}/campi`, data),
    modificaCampo: (id, data) => api.put(`/schede/campi/${id}`, data),
    togliCampo: (id) => api.delete(`/schede/campi/${id}`),
    mie: () => api.get('/schede/mie'),
    creaVariante: (id, data) => api.post(`/schede/${id}/variante`, data),
    variante: (id) => api.get(`/schede/variante/${id}`),
    differenza: (id, data) => api.post(`/schede/variante/${id}/differenze`, data),
    togliDifferenza: (id, diffId) => api.delete(`/schede/variante/${id}/differenze/${diffId}`),
    eliminaVariante: (id) => api.delete(`/schede/variante/${id}`),
};

export const dettaglioAPI = {
    get: (visitaId) => api.get(`/visita-dettaglio/visita/${visitaId}`),
    /* `extra` porta la risposta alla domanda «è la stessa patologia?»
       quando si salva la diagnosi: { patologia: { scelta, voce_id } }. */
    saveCampo: (visitaId, campo, valore, extra = {}) =>
        api.put(`/visita-dettaglio/visita/${visitaId}`, { campo, valore, ...extra }),
    /* Le integrazioni del riquadro «Visita» (migration 236). */
    integrazioni: (visitaId) => api.get(`/visita-dettaglio/visita/${visitaId}/integrazioni`),
    nuovaIntegrazione: (visitaId, testo) => api.post(`/visita-dettaglio/visita/${visitaId}/integrazioni`, { testo }),
    modificaIntegrazione: (id, testo) => api.put(`/visita-dettaglio/integrazioni/${id}`, { testo }),
    /* Le diagnosi della visita, un blocco per diagnosi (migration 256). */
    nuovaDiagnosi: (visitaId) => api.post(`/visita-dettaglio/visita/${visitaId}/diagnosi`),
    salvaDiagnosi: (id, campo, valore, extra = {}) =>
        api.put(`/visita-dettaglio/diagnosi/${id}`, { campo, valore, ...extra }),
    togliDiagnosi: (id) => api.delete(`/visita-dettaglio/diagnosi/${id}`),
};

export const utentiAPI = {
    getAll: () => api.get('/utenti'),
    create: (data) => api.post('/utenti', data),
    setStato: (id, attivo) => api.patch(`/utenti/${id}/stato`, { attivo }),
    resetPassword: (id, password) => api.patch(`/utenti/${id}/password`, { password }),
    azzera2fa: (id) => api.post(`/utenti/${id}/azzera-2fa`),
    reinviaCredenziali: (id) => api.post(`/utenti/${id}/reinvia-credenziali`),
};

export const cestinoAPI = {
    /* `tipo` e `primaDi` (l'id dell'ultima voce mostrata): l'elenco va a
       pagine, e dice se ce ne sono altre (revisione del 3/10/2026). */
    getAll: (tipo, primaDi) => api.get('/cestino', {
        params: { ...(tipo ? { tipo } : {}), ...(primaDi ? { prima_di: primaDi } : {}) },
    }),
    /* `confermaMagazzino`: la risposta al 409 serve_conferma di una visita
       i cui prodotti non possono piu' tornare scaricati (migration 291). */
    ripristina: (id, confermaMagazzino) => api.post(`/cestino/${id}/ripristina`,
        confermaMagazzino ? { conferma_magazzino: true } : {}),
    elimina: (id, conferma) => api.delete(`/cestino/${id}`, { data: { conferma } }),
};

export const anamnesiVociAPI = {
    getByPaziente: (pazienteId, storico = false) =>
        api.get(`/anamnesi-voci/paziente/${pazienteId}`, { params: { storico } }),
    crea: (pazienteId, data) => api.post(`/anamnesi-voci/paziente/${pazienteId}`, data),
    aggiorna: (id, data) => api.put(`/anamnesi-voci/${id}`, data),
    supera: (id, note) => api.post(`/anamnesi-voci/${id}/supera`, { note }),
    riattiva: (id) => api.post(`/anamnesi-voci/${id}/riattiva`),
    cancella: (id) => api.delete(`/anamnesi-voci/${id}`),
};

export const auditAPI = {
    accessi: (params = {}) => api.get('/audit/accessi', { params }),
    riepilogo: () => api.get('/audit/riepilogo'),
    modifiche: (params = {}) => api.get('/audit/modifiche', { params }),
    ripristinaModifica: (id) => api.post(`/audit/modifiche/${id}/ripristina`),
};

export const auditModificheAPI = {
    lista: (params = {}) => api.get('/audit/modifiche', { params }),
};

Object.assign(alertAPI, {
    lista: (params = {}) => api.get('/alert', { params }),
    crea: (data) => api.post('/alert', data),
    completa: (id) => api.post(`/alert/${id}/completa`),
    riapri: (id) => api.post(`/alert/${id}/riapri`),
    segnaLetto: (id) => api.post(`/alert/${id}/letto`),
    rinvia: (id, minuti) => api.post(`/alert/${id}/rinvia`, { minuti }),
});

/* Le liste chiuse dell'iscrizione all'albo (migration 190). */
export const albiAPI = {
    elenco: () => api.get('/albi'),
    aggiungiSede: (codice, dati) => api.post(`/albi/${codice}/sedi`, dati),
    aggiornaSede: (id, dati) => api.put(`/albi/sedi/${id}`, dati),
};
